import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { CheckCircle, Shield, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CITIES } from "@/data/cities";

const heroImages = [
  {
    src: "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6d73b4086db2425fa765b73e4b16b43a?format=webp&width=1200",
    srcset:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6d73b4086db2425fa765b73e4b16b43a?format=webp&width=640 640w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6d73b4086db2425fa765b73e4b16b43a?format=webp&width=1024 1024w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6d73b4086db2425fa765b73e4b16b43a?format=webp&width=1920 1920w",
  },
  {
    src: "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=1200",
    srcset:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=640 640w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=1024 1024w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f9cf7b7fcf34a9ebd604beb27bba026?format=webp&width=1920 1920w",
  },
];

const reviewBadges = [
  {
    name: "Google Reviews",
    image:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F974695d31722451881df8da81294ecef?format=webp&width=200",
  },
  {
    name: "Yelp",
    image:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F9dba0f60d7f44f85a668d42467ae3e5c?format=webp&width=200",
  },
  {
    name: "Angi",
    image:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F344253f3fcbf44e2a62258d754a45b14?format=webp&width=200",
  },
  {
    name: "HomeAdvisor",
    image:
      "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F01063f0ead7548e2ab598d145e79e5be?format=webp&width=200",
  },
];

export default function Index() {
  const { toast } = useToast();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rooms: [] as string[],
    name: "",
    email: "",
    phone: "",
    zipCode: "",
    message: "",
    images: [] as File[],
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleNext = () => {
    setFormStep(formStep + 1);
  };

  const handleBack = () => {
    setFormStep(formStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/.netlify/functions/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "estimate",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          zipCode: formData.zipCode,
          message: formData.message,
          rooms: formData.rooms,
        }),
      });

      if (response.ok) {
        toast({
          title: "Estimate Request Sent!",
          description: "Thank you! We'll contact you soon for your estimate.",
        });
        setFormStep(1);
        setFormData({
          rooms: [],
          name: "",
          email: "",
          phone: "",
          zipCode: "",
          message: "",
          images: [],
        });
      } else {
        throw new Error("Failed to send estimate request");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description:
          "There was an error submitting your request. Please try again or call us directly at (949) 515-7800.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="relative min-h-[600px] md:min-h-[700px] h-auto md:h-screen md:max-h-[1000px] overflow-hidden -mt-[88px]">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="absolute inset-0 bg-black/50 z-10" />
            <img
              src={image.src}
              srcSet={image.srcset}
              sizes="100vw"
              alt={`Specialized Services ${index + 1}`}
              className="w-full h-full object-cover"
              fetchPriority={index === 0 ? "high" : "auto"}
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}

        <div className="relative z-20 container mx-auto px-4 h-full flex items-center justify-center pt-32 md:pt-12 lg:pt-16 pb-12 md:pb-16">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-20 w-full items-center max-w-7xl">
            <div className="text-white space-y-4 md:space-y-6 text-center flex flex-col justify-center">
              <Link
                to="/warranty"
                className="inline-flex items-center justify-center gap-2 mx-auto bg-gradient-to-b from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white px-6 py-2 rounded-full font-semibold text-sm transition-colors shadow-lg"
              >
                <Shield className="w-4 h-4 text-white fill-white" />
                Ask About Our 10 Year Warranty!
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Top Rated Remodeling & Hard Surface Experts
              </h1>
              <p className="text-xl md:text-2xl text-gray-200">
                in Orange County, CA
              </p>
              <p className="text-lg text-gray-300">
                Serving The OC since 1992 with over 15,000 satisfied residential
                & commercial clients!
              </p>
              <p className="text-sm md:text-base text-gray-200 font-medium">
                CA Contractors License #921119 — C-54 / C-15 / B (General Building)
              </p>

              <div className="flex flex-wrap gap-2 md:gap-3 pt-4 justify-center">
                {reviewBadges.map((badge) => (
                  <div key={badge.name} className="flex items-center">
                    <img
                      src={badge.image}
                      alt={badge.name}
                      className="h-14 md:h-20 w-auto object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Card className="bg-white p-4 md:p-6 shadow-2xl w-full max-w-md lg:max-w-sm xl:max-w-md mx-auto lg:mx-0 flex flex-col justify-center">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-brand-gray-dark mb-3 text-center">
                Get a{" "}
                <span
                  className="relative inline-block text-white px-2 py-1 rounded"
                  style={{
                    background: "linear-gradient(to bottom, #e77940, #c96030)",
                  }}
                >
                  FREE
                </span>{" "}
                Estimate!
              </h2>

              <form
                onSubmit={
                  formStep === 3
                    ? handleSubmit
                    : (e) => {
                        e.preventDefault();
                        handleNext();
                      }
                }
              >
                {formStep === 1 && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">
                      What rooms are included in your project?
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newRooms = formData.rooms.includes("kitchen")
                            ? formData.rooms.filter((r) => r !== "kitchen")
                            : [...formData.rooms, "kitchen"];
                          setFormData({ ...formData, rooms: newRooms });
                        }}
                        className={`w-full p-3 border-2 rounded-lg text-left hover:border-brand-orange transition ${
                          formData.rooms.includes("kitchen")
                            ? "border-brand-orange bg-brand-orange/5"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="font-semibold text-brand-gray-dark">
                          Kitchen
                        </div>
                        <div className="text-sm text-gray-600">
                          Remodeling & renovation
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newRooms = formData.rooms.includes("bathroom")
                            ? formData.rooms.filter((r) => r !== "bathroom")
                            : [...formData.rooms, "bathroom"];
                          setFormData({ ...formData, rooms: newRooms });
                        }}
                        className={`w-full p-3 border-2 rounded-lg text-left hover:border-brand-orange transition ${
                          formData.rooms.includes("bathroom")
                            ? "border-brand-orange bg-brand-orange/5"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="font-semibold text-brand-gray-dark">
                          Bathroom
                        </div>
                        <div className="text-sm text-gray-600">
                          Remodeling & renovation
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newRooms = formData.rooms.includes(
                            "hard-surfaces",
                          )
                            ? formData.rooms.filter(
                                (r) => r !== "hard-surfaces",
                              )
                            : [...formData.rooms, "hard-surfaces"];
                          setFormData({ ...formData, rooms: newRooms });
                        }}
                        className={`w-full p-3 border-2 rounded-lg text-left hover:border-brand-orange transition ${
                          formData.rooms.includes("hard-surfaces")
                            ? "border-brand-orange bg-brand-orange/5"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="font-semibold text-brand-gray-dark">
                          Hard Surfaces
                        </div>
                        <div className="text-sm text-gray-600">
                          Flooring, tile, stone, etc.
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newRooms = formData.rooms.includes("other")
                            ? formData.rooms.filter((r) => r !== "other")
                            : [...formData.rooms, "other"];
                          setFormData({ ...formData, rooms: newRooms });
                        }}
                        className={`w-full p-3 border-2 rounded-lg text-left hover:border-brand-orange transition ${
                          formData.rooms.includes("other")
                            ? "border-brand-orange bg-brand-orange/5"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="font-semibold text-brand-gray-dark">
                          Other
                        </div>
                        <div className="text-sm text-gray-600">
                          Tell us about your project
                        </div>
                      </button>
                    </div>
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-brand-orange hover:brightness-90 mt-3"
                      disabled={formData.rooms.length === 0}
                    >
                      Next
                    </Button>
                  </div>
                )}

                {formStep === 2 && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="images">
                        Upload Project Images (Optional)
                      </Label>
                      <Input
                        id="images"
                        type="file"
                        accept="image/*"
                        multiple
                        className="cursor-pointer"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setFormData({ ...formData, images: files });
                        }}
                      />
                      {formData.images.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          {formData.images.length} file
                          {formData.images.length !== 1 ? "s" : ""} selected
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="message" className="mb-2 block">
                        Tell Us More About Your Project
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Please share any details about your project..."
                        className="min-h-[100px]"
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({ ...formData, message: e.target.value })
                        }
                        required
                      />
                      <div className="mt-2 flex items-center justify-between text-sm">
                        <span
                          className={`font-medium ${
                            formData.message.length >= 100
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          {formData.message.length >= 100 ? (
                            <>✓ Ready to continue</>
                          ) : (
                            <>Minimum 100 characters required</>
                          )}
                        </span>
                        <span
                          className={`${
                            formData.message.length >= 100
                              ? "text-green-600"
                              : formData.message.length > 0
                                ? "text-brand-orange"
                                : "text-gray-500"
                          } font-medium`}
                        >
                          {formData.message.length}/100
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        onClick={handleBack}
                        variant="outline"
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-brand-orange hover:brightness-90"
                        disabled={formData.message.length < 100}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                {formStep === 3 && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          required
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="(949) 555-0123"
                          required
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                        />
                      </div>

                      <div>
                        <Label htmlFor="zipCode">Zip Code</Label>
                        <Input
                          id="zipCode"
                          type="text"
                          placeholder="92618"
                          required
                          pattern="[0-9]{5}"
                          maxLength={5}
                          value={formData.zipCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              zipCode: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        onClick={handleBack}
                        variant="outline"
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-brand-orange hover:brightness-90"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Get Free Estimate"}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-gray-dark">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-brand-orange text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">
                Trusted & Reliable
              </h3>
              <p className="text-gray-300">
                With over 30 years of experience, our team delivers dependable
                craftsmanship and honest service you can count on for every
                project.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-brand-orange text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">
                24/7 Response
              </h3>
              <p className="text-gray-300">
                We're here when you need us—day or night. From emergency repairs
                to quick consultations, we respond promptly to keep your project
                on track.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-brand-orange text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">
                5 Star Reviews
              </h3>
              <p className="text-gray-300">
                Our clients love the results. With thousands of 5-star reviews,
                our reputation is built on quality work, professionalism, and
                satisfied customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative order-1 lg:order-1">
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F9acddfc571a243299a7404782b353bcf?format=webp&width=800"
                srcSet="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F9acddfc571a243299a7404782b353bcf?format=webp&width=400 400w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F9acddfc571a243299a7404782b353bcf?format=webp&width=800 800w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F9acddfc571a243299a7404782b353bcf?format=webp&width=1200 1200w"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="What We Do"
                className="rounded-lg shadow-xl w-full h-[300px] md:h-[500px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="order-2 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-gray-dark mb-6">
                What We Do
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At Specialized Services, we transform your vision into reality
                through expert craftsmanship and attention to detail. From
                stunning kitchen transformations to luxurious bathroom
                renovations, we bring over 30 years of experience to every
                project. Our comprehensive services include custom stone and
                tile installations, hardwood flooring, concrete work, and
                specialty hard surface applications. We pride ourselves on
                delivering exceptional quality, transparent communication, and
                results that exceed expectations.
              </p>
              <Link to="/about">
                <Button className="bg-brand-orange hover:brightness-90 text-white px-8 py-6 text-lg">
                  About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-gray-dark mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Since 1992, Specialized Services has been a trusted name in
              kitchen and bathroom remodeling in Orange County. You can trust
              our team of experts to do it right the first time!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <Card className="overflow-hidden hover:shadow-xl transition">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F77011f18dfc44a8a8e761dd55fe36a5d?format=webp&width=600"
                  srcSet="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F77011f18dfc44a8a8e761dd55fe36a5d?format=webp&width=400 400w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F77011f18dfc44a8a8e761dd55fe36a5d?format=webp&width=600 600w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F77011f18dfc44a8a8e761dd55fe36a5d?format=webp&width=800 800w"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt="Kitchen Remodeling in Orange County"
                  className="w-full h-full object-cover hover:scale-105 transition duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3 text-brand-gray-dark">
                  Kitchen Remodeling
                </h3>
                <p className="text-gray-600 mb-4">
                  Upgrade the heart of your home with Specialized Services. We
                  provide expert kitchen remodeling in Orange County, from
                  custom countertops and cabinetry to full layout redesigns. Our
                  team combines craftsmanship and style to create kitchens that
                  are both functional and beautiful.
                </p>
                <Link to="/kitchen-remodeling">
                  <Button className="bg-brand-orange hover:brightness-90">
                    Learn More About Kitchen Remodeling
                  </Button>
                </Link>
              </div>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f010a57889549b697629808c92ac48d?format=webp&width=600"
                  srcSet="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f010a57889549b697629808c92ac48d?format=webp&width=400 400w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f010a57889549b697629808c92ac48d?format=webp&width=600 600w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F3f010a57889549b697629808c92ac48d?format=webp&width=800 800w"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt="Bathroom Remodeling in Orange County"
                  className="w-full h-full object-cover hover:scale-105 transition duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3 text-brand-gray-dark">
                  Bathroom Remodeling
                </h3>
                <p className="text-gray-600 mb-4">
                  Transform your bathroom into a spa-like retreat with
                  Specialized Services. Our expert team specializes in bathroom
                  remodeling in Orange County, offering custom tile work, modern
                  vanities, luxury showers, and more. Whether it's a small
                  upgrade or a complete renovation, we deliver stunning results
                  built to last.
                </p>
                <Link to="/bathroom-remodeling">
                  <Button className="bg-brand-orange hover:brightness-90">
                    Learn More About Bathroom Remodeling
                  </Button>
                </Link>
              </div>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F992b60e4958441d2a6b63cc30f4b1dbc?format=webp&width=600"
                  srcSet="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F992b60e4958441d2a6b63cc30f4b1dbc?format=webp&width=400 400w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F992b60e4958441d2a6b63cc30f4b1dbc?format=webp&width=600 600w, https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F992b60e4958441d2a6b63cc30f4b1dbc?format=webp&width=800 800w"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt="Hard Surfaces in Orange County"
                  className="w-full h-full object-cover hover:scale-105 transition duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3 text-brand-gray-dark">
                  Hard Surfaces
                </h3>
                <p className="text-gray-600 mb-4">
                  From elegant hardwood floors to durable tile and natural
                  stone, Specialized Services delivers premium hard surface
                  installations in Orange County. Our master craftsmen bring
                  over 30 years of expertise in flooring, countertops, and
                  specialty surfaces that enhance your home's beauty and value.
                </p>
                <Link to="/hard-surfaces">
                  <Button className="bg-brand-orange hover:brightness-90">
                    Learn More About Hard Surfaces
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-gray-dark mb-2">
              Proudly Serving Orange County
            </h2>
            <p className="text-gray-600 mb-6">
              Local kitchen remodeling, bathroom remodeling, and hard-surface
              experts serving:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {CITIES.map((city) => (
                <Link
                  key={city.slug}
                  to={`/kitchen-remodeling-${city.slug}`}
                  className="inline-flex items-center gap-2 bg-gray-50 hover:bg-brand-orange hover:text-white border border-gray-200 hover:border-brand-orange px-4 py-2 rounded-full font-medium text-brand-gray-dark transition"
                >
                  <MapPin size={16} />
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-gray-dark text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Experts on Hard Surfaces
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "Hardwood Floor",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcf602bdcff5b4418bbd0ab40e71ac091?format=webp&width=800",
                  path: "/hardwood-floor",
                  alt: "Hardwood flooring installation and refinishing services in Orange County",
                },
                {
                  name: "Flagstone",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcd3f5842a21844ca9d5d79030c5a95be?format=webp&width=800",
                  path: "/flagstone",
                  alt: "Flagstone patio and outdoor surface installation in Orange County",
                },
                {
                  name: "Pavers",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F4c380a2a4526448096f10b51d96625c1?format=webp&width=800",
                  path: "/pavers",
                  alt: "Paver installation and repair services in Orange County",
                },
                {
                  name: "Concrete",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F69808c017311416e98257ff39dbfe9ae?format=webp&width=800",
                  path: "/concrete",
                  alt: "Concrete polishing, grinding, and staining services in Orange County",
                },
                {
                  name: "Marble/Natural Stone",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F913a58de1a874d2a97a3eebb5840971d?format=webp&width=800",
                  path: "/marble-natural-stone",
                  alt: "Marble and natural stone restoration, cleaning, and polishing in Orange County",
                },
                {
                  name: "Tile/Grout",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F90f1ea1c94954242bb646e2757311885?format=webp&width=800",
                  path: "/tile-grout",
                  alt: "Tile and grout cleaning, sealing, and repair services in Orange County",
                },
              ].map((surface) => (
                <Link
                  key={surface.name}
                  to={surface.path}
                  className="group relative overflow-hidden rounded-lg md:aspect-square block cursor-pointer"
                >
                  <img
                    src={surface.image}
                    srcSet={`${surface.image.replace("width=800", "width=400")} 400w, ${surface.image.replace("width=800", "width=600")} 600w, ${surface.image} 800w`}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    alt={surface.alt}
                    className="w-full h-[250px] md:h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                    <div className="p-4 w-full">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <CheckCircle
                          className="text-brand-orange flex-shrink-0"
                          size={20}
                        />
                        {surface.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-orange text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Why Choose Us
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="flex justify-center mb-6">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F8298b608b9d74c76988b4c98c9c1cde8?format=webp&width=200"
                  alt="Trusted & Reliable"
                  className="w-24 h-24 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold mb-4 text-brand-gray-dark">
                Trusted & Reliable
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                With over 30 years of experience, our team delivers dependable
                craftsmanship and honest service you can count on for every
                project.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="flex justify-center mb-6">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F1abbc214046549d3b6ec4525b7c516c0?format=webp&width=200"
                  alt="24/7 Response"
                  className="w-24 h-24 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold mb-4 text-brand-gray-dark">
                24/7 Response
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                We're here when you need us—day or night. From emergency repairs
                to quick consultations, we respond promptly to keep you on
                track.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 text-center shadow-lg">
              <div className="flex justify-center mb-6">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fb699f2ebb0404eb99c476c1ab3852413?format=webp&width=200"
                  alt="5 Star Reviews"
                  className="w-24 h-24 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold mb-4 text-brand-gray-dark">
                5 Star Reviews
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Our clients love the results. With thousands of 5-star reviews,
                our reputation is built on quality work, professionalism, and
                satisfied customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-gray-dark overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-white">
            Our Commercial Customers
          </h2>
          <div className="relative">
            <div className="flex animate-scroll-logos">
              {[
                {
                  name: "South Coast Plaza",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F1a7d0aa29d5640da86327a769f5eacab?format=webp&width=300",
                },
                {
                  name: "Chapman University",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Ff8deaa4a94ce4973bd41e93cdd30de8a?format=webp&width=300",
                },
                {
                  name: "Sheraton",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6f38458eea6346d18e92a57a348cb6c1?format=webp&width=300",
                },
                {
                  name: "Ford",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F830165fd76624440960aeb34b9feebb4?format=webp&width=300",
                },
                {
                  name: "Wells Fargo",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fd864be17ceb941aeb3c1f30d3c5b5c71?format=webp&width=300",
                },
                {
                  name: "UCLA",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F1b0d509e4bb44aeb993fd797b87e9a8d?format=webp&width=300",
                },
                {
                  name: "South Coast Plaza",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F1a7d0aa29d5640da86327a769f5eacab?format=webp&width=300",
                },
                {
                  name: "Chapman University",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Ff8deaa4a94ce4973bd41e93cdd30de8a?format=webp&width=300",
                },
                {
                  name: "Sheraton",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F6f38458eea6346d18e92a57a348cb6c1?format=webp&width=300",
                },
                {
                  name: "Ford",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F830165fd76624440960aeb34b9feebb4?format=webp&width=300",
                },
                {
                  name: "Wells Fargo",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fd864be17ceb941aeb3c1f30d3c5b5c71?format=webp&width=300",
                },
                {
                  name: "UCLA",
                  image:
                    "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2F1b0d509e4bb44aeb993fd797b87e9a8d?format=webp&width=300",
                },
              ].map((customer, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 px-8 flex items-center justify-center"
                  style={{ width: "250px" }}
                >
                  <img
                    src={customer.image}
                    alt={customer.name}
                    className="max-w-full max-h-24 object-contain filter brightness-0 invert opacity-80 hover:opacity-100 transition"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-brand-gray-dark">
              Serving The OC Since 1992
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              We have been a trusted name in kitchen and bathroom remodeling in
              Orange County. We specialize in stone, tile, wood, and
              concrete—offering custom installations & expert repairs.
            </p>
            <p className="text-lg text-gray-600">
              With over 15,000 satisfied clients, we bring decades of experience
              to every project. Serving Orange County, Los Angeles, and San
              Diego, we offer free estimates and tailored advice.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
